import { useState } from 'react';
import { Button } from '../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogScrollArea,
  DialogFooter,
} from '@/components/ui/dialog';
import { 
  Copy, 
  ExternalLink, 
  CheckCircle,
  Zap,
  AlertCircle,
  Book,
  ArrowRight
} from 'lucide-react';
import { BrandIcons } from '../../ui/brand-icons';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

export interface ZapierSuccessIntegration {
  integration_name: string;
  binding_id: string;
  auth_config: {
    api_key: string;
    description?: string;
  };
  space_id: string;
  permissions: any;
}

export interface ZapierSuccessModalContentProps {
  onClose: () => void;
  integration: ZapierSuccessIntegration;
}

export function ZapierSuccessModalContent({
  onClose,
  integration,
}: ZapierSuccessModalContentProps) {
  const { t: _t } = useTranslation();
  const [copiedField, setCopiedField] = useState<string>('');

  // Use environment variable for API base URL with fallback
  const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';
  const zapierBaseUrl = `${apiBaseUrl}/api/v1/zapier`;

  const handleCopy = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      toast.success(`${field} copied to clipboard!`);
      setTimeout(() => setCopiedField(''), 2000);
    } catch (err) {
      toast.error('Failed to copy to clipboard');
    }
  };

  const openZapierDocs = () => {
    window.open('https://zapier.com/app/developer', '_blank');
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
        <DialogHeader className="flex-shrink-0 p-6 pb-4 pr-12">
          <DialogTitle className="text-2xl font-semibold text-gray-900 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            Zapier Integration Created Successfully!
          </DialogTitle>
        </DialogHeader>

        <DialogScrollArea className="space-y-6 px-6">
          {/* Integration Overview */}
          <Card className="border-green-200 bg-green-50/50">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <BrandIcons.zapier className="w-12 h-12" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{integration.integration_name}</h3>
                  <p className="text-gray-600">Ready to connect with your Zapier workflows</p>
                  <Badge className="mt-2 bg-green-100 text-green-700 border-green-200">
                    Integration ID: {integration.binding_id.slice(0, 8)}...
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* API Credentials */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Zap className="w-5 h-5" />
                API Credentials
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-orange-800">Keep these credentials secure!</p>
                    <p className="text-sm text-orange-700">Store them safely - they won't be shown again.</p>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">API Key</label>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 p-3 bg-gray-50 border rounded-lg font-mono text-sm break-all">
                      {integration.auth_config.api_key}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleCopy(integration.auth_config.api_key, 'API Key')}
                      className="flex-shrink-0"
                    >
                      {copiedField === 'API Key' ? (
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Base URL</label>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 p-3 bg-gray-50 border rounded-lg font-mono text-sm">
                      {zapierBaseUrl}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleCopy(zapierBaseUrl, 'Base URL')}
                      className="flex-shrink-0"
                    >
                      {copiedField === 'Base URL' ? (
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Next Steps */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Next Steps</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 border rounded-lg">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center text-sm font-semibold text-orange-700">1</div>
                      <h4 className="font-medium">Add to Zapier</h4>
                    </div>
                    <p className="text-sm text-gray-600 mb-3">
                      Use your API credentials in Zapier to authenticate with Memara
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={openZapierDocs}
                      className="w-full"
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Open Zapier Developer
                    </Button>
                  </div>

                  <div className="p-4 border rounded-lg">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center text-sm font-semibold text-orange-700">2</div>
                      <h4 className="font-medium">Create Workflows</h4>
                    </div>
                    <p className="text-sm text-gray-600 mb-3">
                      Build Zaps that create, search, and manage your memories
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open('https://zapier.com/app/zaps', '_blank')}
                      className="w-full"
                    >
                      <Zap className="w-4 h-4 mr-2" />
                      Browse Zap Templates
                    </Button>
                  </div>
                </div>

                {/* Available Operations */}
                <div className="mt-6">
                  <h4 className="font-medium mb-3">Available Operations</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {integration.permissions.can_write && (
                      <Badge variant="secondary" className="justify-center">Create Memories</Badge>
                    )}
                    {integration.permissions.can_read && (
                      <Badge variant="secondary" className="justify-center">Read Memories</Badge>
                    )}
                    {integration.permissions.can_search && (
                      <Badge variant="secondary" className="justify-center">Search Memories</Badge>
                    )}
                    {integration.permissions.can_delete && (
                      <Badge variant="secondary" className="justify-center">Delete Memories</Badge>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Documentation */}
          <Card className="border-blue-200 bg-blue-50/50">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <Book className="w-5 h-5 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-blue-900">Need Help?</h4>
                  <p className="text-sm text-blue-700">Check out our comprehensive integration guides and API documentation</p>
                </div>
                <Button
                  variant="outline"
                  onClick={() => window.open('/docs', '_blank')}
                  className="border-blue-300 text-blue-700 hover:bg-blue-100"
                >
                  View Documentation
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>

        </DialogScrollArea>
        <DialogFooter variant="scrollable" className="px-6 pb-6">
          <Button
            onClick={onClose}
            className="bg-orange-600 hover:bg-orange-700 text-white"
          >
            Done
          </Button>
        </DialogFooter>
    </div>
  );
}

interface ZapierSuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  integration: ZapierSuccessIntegration;
}

export function ZapierSuccessModal({
  isOpen,
  onClose,
  integration,
}: ZapierSuccessModalProps) {
  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent variant="scrollable" className="max-w-5xl bg-white">
        <ZapierSuccessModalContent integration={integration} onClose={onClose} />
      </DialogContent>
    </Dialog>
  );
}
